import React, { useCallback, useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import { useFetchErrorHookSetter } from "./FetchProvider";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";

type ErrorMessageSetter = (message: string) => void;

const handleUnknownError = (setErrorMessage: ErrorMessageSetter, error: any, intl: IntlShape) => {
    setErrorMessage(intl.formatMessage({ id: "internal_server_error.message" }));
    console.debug("An internal server error occured, but no json with 'message' field came back", error);
};

type DialogTitleId = "internal_server_error.title" | "unreachable_server_error.title";

/**
 * This component listens for errors from FetchProvider and RequestActionCreator, and displays a dialog
 * with the error message.
 * */
const BackendErrorDialog = () => {
    const [show, setShow] = useState(false);
    const [dialogTitleId, setDialogTitleId] = useState<DialogTitleId>("internal_server_error.title");
    const [dialogMessage, setDialogMessage] = useState<string>("");

    const setErrorHook = useFetchErrorHookSetter();
    const intl = useIntl();

    /**
     * The error hook that gets called when an error occurs. It can handle Response, Error and InternalResponse types.
     * It sets the dialog title and message based on the error type.
     * If it encounters an unknown error type, it logs the error and rethrows it.
     * (maybe we should still show the dialog in that case)
     * */
    const errorHook = useCallback(
        async (error: Response | Error) => {
            const handleUnreachableServerError = () => {
                setDialogTitleId("unreachable_server_error.title");
                setDialogMessage(intl.formatMessage({ id: "unreachable_server_error.message" }));
                setShow(true);
            };

            const handleInternalServerError = (message: string) => {
                setDialogTitleId("internal_server_error.title");
                setDialogMessage(message);
                setShow(true);
            };

            console.debug("Error hook called", error);
            if (error instanceof Response) {
                if (error.status === 502 || error.status === 504) {
                    handleUnreachableServerError();
                    setShow(true);
                } else if (error.status >= 500) {
                    const data = await error.json();
                    const message = data.message;
                    if (message) {
                        handleInternalServerError(message);
                    } else {
                        setDialogTitleId("internal_server_error.title");
                        handleUnknownError(setDialogMessage, error, intl);
                        setShow(true);
                    }
                }
            } else if (error instanceof Error) {
                // It is safe to assume that the error has to do with not being able to reach the server
                // so we will use the same method as it was an 502 or 504 error, but just in case
                // we will log the error so it might be easier to debug
                console.error("Error happened during fetch", error);
                handleUnreachableServerError();
            } else {
                console.error("Unknown error type", error);
                throw error;
            }
        },
        [setShow, setDialogMessage, intl],
    );

    useEffect(() => {
        setErrorHook(errorHook);
    }, [errorHook, setErrorHook]);

    return (
        <Dialog open={show} maxWidth="sm" fullWidth>
            <DialogTitle>
                <FormattedMessage id={dialogTitleId} />
            </DialogTitle>
            <DialogContent>{dialogMessage}</DialogContent>
            <DialogActions>
                <Button onClick={() => window.location.reload()} variant="contained" color="primary">
                    <FormattedMessage id="reload_page" />
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BackendErrorDialog;
